import { logger } from "../../utils/logger";
import React, { useState, useEffect } from 'react';
import { AppPageHeader } from '../../components/app/AppPage';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent
} from '@mui/material';
import { Add as AddIcon, PersonAdd as PersonAddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { userService, CompanyUser, CompanyUserCreate } from '../../services/userService';
import { useSnackbar } from 'notistack';
import { Company } from '../../types/company';
import { useAuth } from '../../contexts/AuthContext';
import { validateEmail } from '../../utils/validation';

export const CompanyUserList: React.FC = () => {
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newUser, setNewUser] = useState<CompanyUserCreate>({
    email: '',
    password: '',
    full_name: '',
    is_active: true
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [editUser, setEditUser] = useState<CompanyUser | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<CompanyUser | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  useEffect(() => {
    fetchUsers();
    // Set current user from auth context
    setCurrentUser(user);
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getCompanyUsers();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      logger.error('Error loading company users:', err);
      setError('Failed to load company users. Please try again later.');
      enqueueSnackbar('Error loading company users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setNewUser({
      email: '',
      password: '',
      full_name: '',
      is_active: true,
      role: 'user'
    });
    setFormErrors({});
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewUser({
      ...newUser,
      [name]: value
    });
    
    // Real-time validation for email
    if (name === 'email') {
      if (!value.trim()) {
        setFormErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else if (!validateEmail(value)) {
        setFormErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setFormErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
      }
    } else {
      // Clear validation error when user starts typing for other fields
      if (formErrors[name]) {
        const { [name]: _, ...rest } = formErrors;
        setFormErrors(rest);
      }
    }
  };
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setNewUser({
      ...newUser,
      [name]: value
    });
    if (formErrors[name]) {
      const { [name]: _, ...rest } = formErrors;
      setFormErrors(rest);
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!newUser.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(newUser.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Only require password for new users, not when editing
    if (!editUser) {
      if (!newUser.password.trim()) {
        errors.password = 'Password is required';
      } else if (newUser.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
    } else if (newUser.password && newUser.password.length < 8) {
      // If password is provided during edit, validate length
      errors.password = 'Password must be at least 8 characters';
    }

    if (!newUser.role) {
      errors.role = 'Role is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    // For edit mode, password is optional
    if (editUser) {
      return !formErrors.email && !formErrors.role &&
             newUser.email.trim() && newUser.role &&
             (!newUser.password || !formErrors.password);
    }
    // For new user, all fields including password are required
    return !formErrors.email && !formErrors.password && !formErrors.role &&
           newUser.email.trim() && newUser.password.trim() && newUser.role;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    // Remove company_id from the data sent to backend since it uses current user's company
    const { company_id, ...userDataToSend } = newUser;
    
    try {
      const createdUser = await userService.createCompanyUser(userDataToSend);
      setUsers([...users, createdUser]);
      enqueueSnackbar('User added successfully', { variant: 'success' });
      handleCloseDialog();
    } catch (err: any) {
      logger.error('Error creating user:', err);
      logger.error('Error response:', err.response?.data);
      if (err.response?.data?.detail) {
        enqueueSnackbar(`Failed to create user: ${err.response.data.detail}`, { variant: 'error' });
      } else if (err.response?.data?.message) {
        enqueueSnackbar(`Failed to create user: ${err.response.data.message}`, { variant: 'error' });
      } else {
        enqueueSnackbar('Failed to create user', { variant: 'error' });
      }
    }
  };

  const handleEditUser = (user: CompanyUser) => {
    setEditUser(user);
    setOpenDialog(true);
    setNewUser({
      email: user.email,
      password: '',
      full_name: user.full_name || '',
      role: user.role || 'user'
    });
  };

  const handleDeleteUser = (user: CompanyUser) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await userService.deleteCompanyUser(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      enqueueSnackbar('User deleted successfully', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to delete user', { variant: 'error' });
    } finally {
      setOpenDeleteDialog(false);
      setUserToDelete(null);
    }
  };

  const handleEditSubmit = async () => {
    if (!editUser) return;
    if (!validateForm()) return;
    try {
      const updated = await userService.updateCompanyUser(editUser.id, newUser);
      setUsers(users.map(u => (u.id === editUser.id ? updated : u)));
      enqueueSnackbar('User updated successfully', { variant: 'success' });
      setEditUser(null);
      handleCloseDialog();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to update user', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="gt-page">
      <AppPageHeader
        title="Company users"
        description="People who can sign in to this company, and what they can change."
        actions={<Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add User
        </Button>}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Full Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    No users found. Add users to your company by clicking the "Add User" button.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.full_name || '-'}</TableCell>
                  <TableCell>{companies.find((c: Company) => c.id === user.company_id)?.name || user.company_id || '-'}</TableCell>
                  <TableCell>{user.role ? (user.role === 'admin' ? 'Admin' : 'Normal') : (user.is_company_admin ? 'Admin' : 'Normal')}</TableCell>
                  <TableCell>{user.is_active ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <span>
                        <IconButton onClick={() => handleEditUser(user)} size="small" disabled={!currentUser?.is_company_admin}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton onClick={() => handleDeleteUser(user)} size="small" color="error" disabled={!currentUser?.is_company_admin || user.id === currentUser?.id} title={user.id === currentUser?.id ? "You can't delete your own account" : undefined}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editUser ? 'Edit Company User' : 'Add Company User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              name="full_name"
              value={newUser.full_name}
              onChange={handleTextInputChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={newUser.email}
              onChange={handleTextInputChange}
              margin="normal"
              required
              error={!!formErrors.email}
              helperText={formErrors.email}
              type="email"
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={newUser.password}
              onChange={handleTextInputChange}
              margin="normal"
              required={!editUser}
              error={!!formErrors.password}
              helperText={formErrors.password}
            />
            <FormControl fullWidth margin="normal" error={!!formErrors.role}>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                name="role"
                value={newUser.role || 'user'}
                onChange={handleSelectChange}
                label="Role"
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="user">User</MenuItem>
              </Select>
              {formErrors.role && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {formErrors.role}
                </Typography>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={editUser ? handleEditSubmit : handleSubmit} color="primary" variant="contained" disabled={!isFormValid()}>
            {editUser ? 'Update User' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Warning Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={confirmDeleteUser} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyUserList; 